import React, { useState, useRef, useEffect, useCallback } from 'react';
import CryptoJS from 'crypto-js';
import { api, WebSocketCredentials } from '../../services/api';

interface ShoulderTapEvent {
  session_id: string;
  actor: string;
  message: string;
  reasoning: string;
  sentiment_trend: string;
  evidence: string[];
  timestamp: string;
  routing_decision: string;
  effective_tier: string;
  escalated: boolean;
  needs_rph: boolean;
  escalation_flag: boolean;
  sentiment_score: number;
  degraded: boolean;
  // Two-sentence-max summary the agent UI surfaces directly. Empty
  // when the FM did not flag needs_rph for this evaluation.
  tap_message: string;
  // Did this evaluation actually raise a tap (after latch + dedup)?
  // Every classification is pushed for dev observability; this flag
  // tells us which ones to highlight.
  tap_fired: boolean;
}

interface ShoulderTapPageProps {
  user: { authenticated: boolean };
}

const ShoulderTapPage: React.FC<ShoulderTapPageProps> = ({ user }) => {
  const [contactId, setContactId] = useState('');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [events, setEvents] = useState<ShoulderTapEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pingRef = useRef<NodeJS.Timeout | null>(null);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    if (pingRef.current) clearInterval(pingRef.current);
    pingRef.current = null;
    setConnected(false);
  }, []);

  useEffect(() => () => disconnect(), [disconnect]);

  const signUrl = (credentials: WebSocketCredentials, cId: string): string => {
    const wsUrl = credentials.websocketUrl.replace('https://', 'wss://');
    const url = new URL(wsUrl);
    const { host, pathname: path } = url;
    const { region } = credentials;
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:\-]|\.\d{3}/g, '');
    const date = timestamp.substr(0, 8);

    const params: Record<string, string> = {
      'X-Amz-Algorithm': 'AWS4-HMAC-SHA256',
      'X-Amz-Credential': `${credentials.accessKeyId}/${date}/${region}/execute-api/aws4_request`,
      'X-Amz-Date': timestamp,
      'X-Amz-SignedHeaders': 'host',
      'contact_id': cId,
    };
    if (credentials.sessionToken) params['X-Amz-Security-Token'] = credentials.sessionToken;

    const canonicalQuery = Object.keys(params).sort()
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`).join('&');
    const canonicalRequest = `GET\n${path}\n${canonicalQuery}\nhost:${host}\n\nhost\n${CryptoJS.SHA256('').toString()}`;
    const stringToSign = `AWS4-HMAC-SHA256\n${timestamp}\n${date}/${region}/execute-api/aws4_request\n${CryptoJS.SHA256(canonicalRequest).toString()}`;

    const kDate = CryptoJS.HmacSHA256(date, 'AWS4' + credentials.secretAccessKey);
    const kRegion = CryptoJS.HmacSHA256(region, kDate);
    const kService = CryptoJS.HmacSHA256('execute-api', kRegion);
    const kSigning = CryptoJS.HmacSHA256('aws4_request', kService);
    params['X-Amz-Signature'] = CryptoJS.HmacSHA256(stringToSign, kSigning).toString();

    const finalQuery = Object.keys(params).sort()
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`).join('&');
    return `${wsUrl}?${finalQuery}`;
  };

  const connect = async () => {
    if (!contactId.trim()) return;
    setConnecting(true);
    setError(null);
    setEvents([]);

    try {
      const credentials = await api.getWebSocketCredentials();
      if (!credentials) throw new Error('Failed to get WebSocket credentials');

      const signedUrl = signUrl(credentials, contactId.trim());
      const ws = new WebSocket(signedUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setConnecting(false);
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: 'ping' }));
        }, 60000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'shoulder_tap_event') {
            const data: ShoulderTapEvent = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
            setEvents(prev => [...prev, data]);
          }
        } catch (e) {
          // ignore non-shoulder-tap messages
        }
      };

      ws.onclose = () => { setConnected(false); setConnecting(false); };
      ws.onerror = () => { setError('WebSocket connection failed'); setConnecting(false); };
    } catch (e: any) {
      setError(e.message || 'Connection failed');
      setConnecting(false);
    }
  };

  const tierColor = (tier: string) => {
    if (tier === 'rph') return '#ff6b6b';
    if (tier === 'crt') return '#6b6bff';
    return '#6bff6b';
  };

  return (
    <div style={{ fontFamily: '-apple-system, sans-serif', background: '#0f1117', color: '#e0e0e0', minHeight: '100vh', padding: 24 }}>
      <h1 style={{ fontSize: 20, marginBottom: 16 }}>🩺 Shoulder Tap Live Monitor</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input
          type="text"
          placeholder="Enter Contact ID"
          value={contactId}
          onChange={e => setContactId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && connect()}
          disabled={connected}
          style={{ flex: 1, maxWidth: 400, padding: '8px 12px', background: '#2a2d3a', border: '1px solid #3a3d4a', color: '#e0e0e0', borderRadius: 6, fontSize: 14 }}
        />
        {!connected ? (
          <button onClick={connect} disabled={connecting || !contactId.trim()}
            style={{ padding: '8px 16px', background: '#3a5a8a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        ) : (
          <button onClick={disconnect}
            style={{ padding: '8px 16px', background: '#5a3a3a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
            Disconnect
          </button>
        )}
      </div>

      {error && <div style={{ color: '#ff6b6b', marginBottom: 16 }}>{error}</div>}

      {connected && events.length === 0 && (
        <div style={{ color: '#888', fontStyle: 'italic' }}>Listening for shoulder tap events on {contactId}...</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {events.map((evt, i) => {
          const tapped = evt.tap_fired;
          const cardStyle: React.CSSProperties = tapped
            ? {
                background: '#231a0e',
                border: '2px solid #ffb84a',
                borderRadius: 8,
                padding: 16,
                boxShadow: '0 0 12px rgba(255, 184, 74, 0.25)',
              }
            : {
                background: '#15171f',
                border: '1px solid #2a2d3a',
                borderRadius: 8,
                padding: 16,
                opacity: 0.78,
              };
          return (
            <div key={i} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{evt.actor}</span>
                  {tapped && (
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, background: '#ffb84a', color: '#1a1300' }}>
                      TAP
                    </span>
                  )}
                  {!tapped && (
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, background: '#1a1d27', color: '#666', border: '1px solid #2a2d3a' }}>
                      shadow
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 12, color: '#888' }}>{evt.timestamp}</span>
              </div>
              {tapped && evt.tap_message && (
                <div style={{ marginBottom: 12, padding: '10px 12px', background: '#1a1300', border: '1px solid #ffb84a55', borderRadius: 6, color: '#ffd98a', fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>
                  {evt.tap_message}
                </div>
              )}
              <div style={{ marginBottom: 8, color: '#b0b8d0' }}>{evt.message}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#1a1a2a', color: tierColor(evt.effective_tier), border: `1px solid ${tierColor(evt.effective_tier)}33` }}>
                  {evt.effective_tier.toUpperCase()}
                </span>
                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, background: '#1a2a1a', color: '#aaa' }}>
                  sentiment: {evt.sentiment_score.toFixed(2)} ({evt.sentiment_trend})
                </span>
                {evt.escalated && <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, background: '#3a1a1a', color: '#ff6b6b' }}>ESCALATED</span>}
                {evt.degraded && <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, background: '#3a3a1a', color: '#ffff6b' }}>DEGRADED</span>}
              </div>
              <div style={{ fontSize: 13, color: '#999', marginBottom: 4 }}><strong>Reasoning:</strong> {evt.reasoning}</div>
              {evt.evidence.length > 0 && (
                <div style={{ fontSize: 12, color: '#777', fontStyle: 'italic' }}>
                  Evidence: {evt.evidence.map((e, j) => <span key={j}>"{e}" </span>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShoulderTapPage;
